// Frontend mirror of the API's data model (api/src/models/activity.types.ts).

export type ActivityType =
  | 'speaking'
  | 'workshop'
  | 'video'
  | 'livestream'
  | 'podcast'
  | 'other';

/** Source is now an open lowercase platform slug (no closed union). */
export type ActivitySource = string;

export interface ActivityLocation {
  place_name: string;
  city?: string;
  region?: string;
  country?: string;
  country_code?: string;
  latitude?: number;
  longitude?: number;
  /** true = typed by hand / unmappable (no coords). */
  is_manual?: boolean;
}

/**
 * What a piece of content IS, as opposed to how it performed. Mirrors
 * api/src/models/activity.types.ts. Optional everywhere: absent on activities
 * created before the Insights feature, and on sources we could not read.
 */
export interface ContentDna {
  format?: string;
  hook_type?: string;
  topic?: string;
  tone?: string;
  audience?: string;
  cta?: string;
  dna_version?: string;
}

/** Provenance for an activity produced from an insight. */
export interface ActivityOrigin {
  made_from_insight_id: string;
  created_at?: string;
}

/** A single metric inside a snapshot. */
export interface Metric {
  /** e.g. 'views', 'likes', 'newsletter_signups'. */
  label: string;
  /** This metric's number. */
  reach: number;
  /** Present/true ONLY when manually entered (absent = auto-crawled). */
  is_manual?: boolean;
}

/** One time-stamped snapshot of an activity's metrics (newest = current). */
export interface MetricReading {
  captured_date: string;
  /** Array of metrics — the ONLY source of truth for reach. */
  metrics: Metric[];
  fetched_via?: string;
  fetch_duration_ms?: number;
  /** Present/true ONLY when the auto-crawl failed (auto values stale). */
  crawl_failed?: boolean;
}

export interface Activity {
  _id?: string;
  owner_user_id?: string;
  title: string;
  description?: string;
  type: ActivityType;
  date: string;
  url?: string;
  slides_url?: string;
  thumbnail?: string;
  is_online?: boolean;
  location?: ActivityLocation;
  /** Free-form topic tags, max 5. */
  tags?: string[];
  /** Optional platform slug, resolved API-side. */
  source?: string;
  /** Time-series of metric snapshots; the last entry is the current one. */
  metrics_list: MetricReading[];
  created_date?: string;
  updated_date?: string;

  /** Structured description of what this content is (see ContentDna). */
  content_dna?: ContentDna;

  /** Set only when this activity was created because of an insight. */
  origin?: ActivityOrigin;

}

export interface ActivityDraft {
  url: string;
  source?: string;
  title?: string;
  description?: string;
  type?: ActivityType;
  date?: string;
  thumbnail?: string;
  /** The crawled metrics as an array (each auto value carries is_manual:false). */
  metrics: Metric[];
  /** false when reach couldn't be measured automatically → ask for manual entry. */
  reach_known: boolean;
  fetched_via: string;
  fetch_duration_ms?: number;
}

/** Payload to create an activity (one initial snapshot + metadata). */
export interface CreateActivityPayload {
  title: string;
  type: ActivityType;
  date: string;
  description?: string;
  url?: string;
  slides_url?: string;
  thumbnail?: string;
  source?: string;
  is_online?: boolean;
  /** Initial snapshot metrics (crawled is_manual:false, hand-entered true). */
  metrics?: Metric[];
  fetched_via?: string;
  fetch_duration_ms?: number;
  location?: ActivityLocation;
  tags?: string[];
}

export interface ReachSummary {
  total_reach: number;
  count: number;
  by_source: { source: string; reach: number; count: number }[];
  by_type: { type: ActivityType; reach: number; count: number }[];
}

export interface SourceDefinition {
  source: string;
  label: string;
  /** The default suggested metric labels for this source. */
  metrics: string[];
}

/** A normalized geocode result from the API (Photon-backed). */
export interface LocationSuggestion {
  place_name: string;
  city?: string;
  region?: string;
  country?: string;
  country_code?: string;
  latitude: number;
  longitude: number;
}

// ── Reach helpers — reach is ALWAYS derived, never stored. ──────────────────

/** Sum a snapshot's metric reach values. */
export function reachOf(reading: MetricReading | undefined | null): number {
  if (!reading?.metrics) return 0;
  return reading.metrics.reduce((s, m) => s + (m.reach || 0), 0);
}

/** Current reach of an activity = reach of its latest snapshot. */
export function currentReach(activity: Activity | undefined | null): number {
  const list = activity?.metrics_list;
  if (!list || !list.length) return 0;
  return reachOf(list[list.length - 1]);
}

/**
 * Verified reach = the crawled (auto) portion only. Crawled numbers come
 * straight from the source, so they're the verified, trustworthy figure;
 * manually-entered metrics are excluded here.
 */
export function verifiedReachOf(
  reading: MetricReading | undefined | null,
): number {
  if (!reading?.metrics) return 0;
  return reading.metrics
    .filter((m) => !m.is_manual)
    .reduce((s, m) => s + (m.reach || 0), 0);
}

/** Current verified (crawled-only) reach of an activity's latest snapshot. */
export function currentVerifiedReach(
  activity: Activity | undefined | null,
): number {
  const list = activity?.metrics_list;
  if (!list || !list.length) return 0;
  return verifiedReachOf(list[list.length - 1]);
}
